import { Alert, Form, Modal, Radio } from 'antd';
import { useState } from 'react';

import {
  isNightsWithoutRevenueShareError,
  useMigrateHotel,
} from 'app/hooks/data/core/admin/hotels/useMigrateHotel';
import type { RoomWithoutRevenueShare } from 'app/hooks/data/core/admin/hotels/useMigrateHotel';
import type { HotelInfo } from 'app/typings/hotels/Hotel/Hotel';

import styles from './MigrateModal.module.scss';

type MigrateModalProps = {
  open: boolean;
  onClose: () => void;
  hotel: HotelInfo;
};

type NightsWithoutRevenueShareWarning = {
  hotelId: number;
  message: string;
  rooms: RoomWithoutRevenueShare[];
};

export const MigrateModal = ({ open, onClose, hotel }: MigrateModalProps) => {
  const [form] = Form.useForm();
  const { mutateAsync: migrateHotel, isPending: isMigrating } =
    useMigrateHotel();
  const [warning, setWarning] = useState<NightsWithoutRevenueShareWarning>();
  const nightsWithoutRevenueShareWarning =
    warning?.hotelId === hotel.id ? warning : undefined;

  const handleOk = () => {
    form.submit();
  };

  const handleClose = () => {
    setWarning(undefined);
    onClose();
  };

  const onSubmit = async ({
    emailConfirmation,
  }: {
    emailConfirmation: boolean;
  }) => {
    const hotelId = hotel.id;

    try {
      await migrateHotel({
        hotelId,
        emailConfirmation,
        acceptedNightsWithoutRevenueShare:
          nightsWithoutRevenueShareWarning?.rooms.map(
            ({ oldRoomId, dates }) => ({ oldRoomId, dates })
          ) ?? [],
      });
    } catch (error) {
      if (!isNightsWithoutRevenueShareError(error)) {
        throw error;
      }

      setWarning({
        hotelId,
        message: error.jsonObject.errorMessage,
        rooms: error.jsonObject.data?.rooms ?? [],
      });

      return;
    }

    handleClose();
  };

  return (
    <Modal
      title="Migrate hotel to the new data model?"
      open={open}
      width={572}
      closable={!isMigrating}
      maskClosable={!isMigrating}
      keyboard={!isMigrating}
      destroyOnClose
      onCancel={handleClose}
      onOk={handleOk}
      confirmLoading={isMigrating}
      cancelButtonProps={{ disabled: isMigrating }}
      okText={
        nightsWithoutRevenueShareWarning === undefined
          ? 'Migrate hotel'
          : 'Migrate anyway'
      }
      centered
      className={styles.migrateHotelModal}
    >
      <div className={styles.migrateHotelModalContent}>
        <p>
          Are you sure you want to migrate this hotel to the new data model?
        </p>
        <br />
        <p>This action will migrate :</p>
        <ul>
          <li>
            all published night packages, including their associated rooms and
            activities.
          </li>
          <li>
            all published day packages,{' '}
            <strong>excluding their associated activities.</strong>
          </li>
        </ul>
      </div>

      <div className={styles.confirmation}>
        <p>
          Would you like to send migration confirmation emails when migrating
          this hotel?
        </p>
        <Form form={form} onFinish={onSubmit}>
          <Form.Item
            name="emailConfirmation"
            rules={[{ required: true, message: 'Please select an option' }]}
          >
            <Radio.Group>
              <Radio value={true}>Migrate and send confirmation emails.</Radio>
              <Radio value={false}>
                Migrate without sending confirmation emails.
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </div>
      <p>This action cannot be undone.</p>
      {nightsWithoutRevenueShareWarning !== undefined && (
        <Alert
          type="warning"
          showIcon
          message={nightsWithoutRevenueShareWarning.message}
          description={
            <ul className={styles.roomsWithoutRevenueShare}>
              {nightsWithoutRevenueShareWarning.rooms.map(
                ({ oldRoomId, roomName, dates }) => (
                  <li key={oldRoomId}>
                    {roomName ?? 'Unnamed room'}: {dates.length} night(s),{' '}
                    {dates[0]} → {dates[dates.length - 1]}
                  </li>
                )
              )}
            </ul>
          }
        />
      )}
    </Modal>
  );
};
